<script setup lang="ts">
import Quill from "quill";
import { useWordProcessorStore } from "../useWordProcessorStore";

const wordProcessorInstanceId = inject("instanceId") as string;
const wordProcessorStore = useWordProcessorStore(wordProcessorInstanceId);
const { selectionRange, quillDelta } = storeToRefs(wordProcessorStore);
const editorRef = inject("quillEditor") as any;

const DEFAULT_FONT_SIZE_PT = 12;
const fontSizeInput = ref(String(DEFAULT_FONT_SIZE_PT));

const parseSizeToNumber = (sizeValue: unknown) => {
  if (sizeValue == undefined || sizeValue === false) return null;

  const normalizedValue = String(sizeValue).trim().toLowerCase();
  const numericValue = Number.parseFloat(
    normalizedValue.replace("pt", "").replace("px", "")
  );

  if (Number.isNaN(numericValue) || numericValue <= 0) return null;
  return numericValue;
};

const normalizeSizeForInput = (size: number) => {
  return Number.isInteger(size)
    ? String(size)
    : String(Number(size.toFixed(2)));
};

const getTargetRange = () => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return null;

  const liveSelection = quill.getSelection();
  if (liveSelection) return liveSelection;

  return selectionRange.value ?? null;
};

const syncInputWithSelection = () => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  const selection = selectionRange.value;
  if (!selection) return;

  const format = quill.getFormat(selection.index, selection.length);
  const size = parseSizeToNumber(format.fontSize ?? format.size);
  fontSizeInput.value = normalizeSizeForInput(size ?? DEFAULT_FONT_SIZE_PT);
};

watch([selectionRange, quillDelta], syncInputWithSelection, {
  immediate: true,
});

const setFontSize = (rawSize: string) => {
  const quill = editorRef.value?.getQuill?.();
  if (!quill) return;

  const selection = getTargetRange();
  if (!selection) return;

  const parsedSize = parseSizeToNumber(rawSize);
  if (!parsedSize) {
    quill.format("fontSize", false, Quill.sources.USER);
    fontSizeInput.value = String(DEFAULT_FONT_SIZE_PT);
    return;
  }

  const normalizedSize = normalizeSizeForInput(parsedSize);
  const fontSize = `${normalizedSize}pt`;

  if (selection.length > 0) {
    quill.formatText(
      selection.index,
      selection.length,
      "fontSize",
      fontSize,
      Quill.sources.USER
    );
    quill.setSelection(selection.index, selection.length, Quill.sources.SILENT);
  } else {
    quill.setSelection(selection.index, 0, Quill.sources.SILENT);
    quill.format("fontSize", fontSize, Quill.sources.USER);
  }

  fontSizeInput.value = normalizedSize;
};

const stepFontSize = (delta: number) => {
  const currentSize =
    parseSizeToNumber(fontSizeInput.value) ?? DEFAULT_FONT_SIZE_PT;
  const nextSize = Math.max(1, Math.round(currentSize + delta));
  setFontSize(String(nextSize));
};

const onBlur = () => {
  setFontSize(fontSizeInput.value);
};

const onEnter = (event: KeyboardEvent) => {
  if (event.key !== "Enter") return;
  event.preventDefault();
  setFontSize(fontSizeInput.value);
};

const hasSelectedText = computed(() => {
  return (
    selectionRange.value?.length != undefined &&
    selectionRange.value?.length > 1
  );
});
</script>

<template>
  <div class="h-[30px] flex items-center shrink-0 border rounded">
    <span
      type="button"
      tabindex="-1"
      aria-label="Decrease font size"
      class="h-full text-[10px] hover:bg-light px-1 flex flex-col items-center justify-center"
      @mousedown.prevent
      @click="stepFontSize(-1)"
      :class="{
        'cursor-not-allowed': hasSelectedText != true,
        'cursor-pointer': hasSelectedText == true,
      }"
    >
      <BaseIconSubtract />
    </span>
    <span
      class="flex items-center justify-center w-[36px] h-full bg-surface px-1 text-xs text-center border-r border-l cursor-not-allowed"
    >
      {{ fontSizeInput }}pt
    </span>
    <span
      type="button"
      tabindex="-1"
      aria-label="Increase font size"
      class="h-full text-[10px] hover:bg-light px-1 flex flex-col items-center justify-center"
      @mousedown.prevent
      @click="stepFontSize(1)"
      :class="{
        'cursor-not-allowed': hasSelectedText != true,
        'cursor-pointer': hasSelectedText == true,
      }"
    >
      <BaseIconPlus />
    </span>
  </div>
</template>

<style scoped>
.font-size-input::-webkit-outer-spin-button,
.font-size-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.font-size-input[type="number"] {
  -moz-appearance: textfield;
}
</style>
